import { useMemo } from 'react'

type ParsedColor = {
  hex: string
  alpha: number
  css: string
}

interface ColorPickerFieldProps {
  label: string
  value: string | null | undefined
  fallback?: string
  onCommit: (value: string) => void
  className?: string
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

function componentToHex(value: number): string {
  return clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0')
}

function rgbaToCss(r: number, g: number, b: number, a: number): string {
  const alpha = Number(clamp(a, 0, 1).toFixed(3))
  return `rgba(${clamp(r, 0, 255)}, ${clamp(g, 0, 255)}, ${clamp(b, 0, 255)}, ${alpha})`
}

function hexToRgb(hex: string): { r: number; g: number; b: number; a?: number } | null {
  const raw = hex.replace('#', '').trim()
  if (raw.length === 3 || raw.length === 4) {
    const r = parseInt(raw[0] + raw[0], 16)
    const g = parseInt(raw[1] + raw[1], 16)
    const b = parseInt(raw[2] + raw[2], 16)
    const a = raw.length === 4 ? parseInt(raw[3] + raw[3], 16) / 255 : undefined
    return { r, g, b, a }
  }
  if (raw.length === 6 || raw.length === 8) {
    const r = parseInt(raw.slice(0, 2), 16)
    const g = parseInt(raw.slice(2, 4), 16)
    const b = parseInt(raw.slice(4, 6), 16)
    const a = raw.length === 8 ? parseInt(raw.slice(6, 8), 16) / 255 : undefined
    return { r, g, b, a }
  }
  return null
}

function parseCssColor(value: string | null | undefined, fallback = 'rgba(37, 99, 235, 1)'): ParsedColor {
  const source = (value || fallback).trim()
  const hexMatch = source.match(/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i)
  if (hexMatch) {
    const rgb = hexToRgb(source)
    if (rgb) {
      const alpha = rgb.a ?? 1
      const hex = `#${componentToHex(rgb.r)}${componentToHex(rgb.g)}${componentToHex(rgb.b)}`
      return { hex, alpha, css: rgbaToCss(rgb.r, rgb.g, rgb.b, alpha) }
    }
  }

  const rgbaMatch = source.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i)
  if (rgbaMatch) {
    const r = Number(rgbaMatch[1])
    const g = Number(rgbaMatch[2])
    const b = Number(rgbaMatch[3])
    const alpha = rgbaMatch[4] === undefined ? 1 : Number(rgbaMatch[4])
    const hex = `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`
    return { hex, alpha: clamp(alpha, 0, 1), css: rgbaToCss(r, g, b, alpha) }
  }

  if (source !== fallback) return parseCssColor(fallback, fallback)
  return { hex: '#2563eb', alpha: 1, css: 'rgba(37, 99, 235, 1)' }
}

function colorFromHexAndAlpha(hex: string, alpha: number): ParsedColor {
  const rgb = hexToRgb(hex) ?? { r: 37, g: 99, b: 235 }
  const normalizedHex = `#${componentToHex(rgb.r)}${componentToHex(rgb.g)}${componentToHex(rgb.b)}`
  return {
    hex: normalizedHex,
    alpha: clamp(alpha, 0, 1),
    css: rgbaToCss(rgb.r, rgb.g, rgb.b, alpha),
  }
}

export function ColorPickerField({
  label,
  value,
  fallback = 'rgba(37, 99, 235, 1)',
  onCommit,
  className = '',
}: ColorPickerFieldProps) {
  const parsed = useMemo(() => parseCssColor(value, fallback), [value, fallback])

  const commitParsed = (next: ParsedColor) => {
    onCommit(next.css)
  }

  return (
    <div className={`space-y-1 ${className}`}>
      <span className="block text-xs font-medium text-gray-600">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={parsed.hex}
          onChange={(event) => commitParsed(colorFromHexAndAlpha(event.target.value, parsed.alpha))}
          className="h-9 w-11 shrink-0 rounded border border-gray-300 bg-white p-1"
        />
        <div
          className="h-9 flex-1 rounded border border-gray-300"
          style={{
            background:
              'linear-gradient(45deg, #d1d5db 25%, transparent 25%), linear-gradient(-45deg, #d1d5db 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d1d5db 75%), linear-gradient(-45deg, transparent 75%, #d1d5db 75%)',
            backgroundColor: '#fff',
            backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
            backgroundSize: '12px 12px',
          }}
        >
          <div className="h-full rounded" style={{ backgroundColor: parsed.css }} />
        </div>
      </div>
      <label className="flex items-center gap-2 text-[11px] text-gray-500">
        <span className="w-16">Прозр.</span>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(parsed.alpha * 100)}
          onChange={(event) => commitParsed(colorFromHexAndAlpha(parsed.hex, Number(event.target.value) / 100))}
          className="flex-1"
        />
        <span className="w-9 text-right">{Math.round(parsed.alpha * 100)}%</span>
      </label>
    </div>
  )
}
